import { Card, Descriptions, Flex, Input } from 'antd';

import { Schemas } from '@api-client/generated/types';
import { CopyButton } from '@components';

import { getCountryDataByCode } from '../../helpers';
import { renderFiles } from '../helpers';
import * as S from './styled';

type Person = Schemas.PersonResponseDto;

type PersonCardProps = {
  person: Person;
};

/**
 * One natural person on the Onboarding tab, read-only.
 *
 * Assigning the UBO number was the last write on this card. It is ours rather than the client's,
 * but it is still a field on a person record the backoffice no longer owns — so it goes with the
 * rest, and an unassigned number now reads as "—" instead of offering a button.
 */
export const PersonCard = ({ person }: PersonCardProps) => {
  if (!person) {
    return <S.Empty>Select a person</S.Empty>;
  }

  const uboNumber = person.uboNumber
    ? `E${person.uboNumber.toString().padStart(6, '0')}`
    : null;

  return (
    <S.Container>
      <S.Header align="center" justify="space-between">
        <S.Title level={4}>{person.name}</S.Title>
      </S.Header>

      <Flex gap={8} vertical>
        <Card title="Basic info">
          <Descriptions
            column={2}
            items={[
              {
                key: 'personalPhone',
                label: 'Personal phone',
                span: 2,
                children: person.kycData?.personalPhone ?? '-',
              },
              {
                key: 'personalEmail',
                label: 'Email',
                span: 2,
                children: person.kycData?.personalEmail ?? '-',
              },
              {
                key: 'nationalities',
                label: 'Nationalities',
                span: 2,
                children: person.kycData?.nationality
                  ? getCountryDataByCode(person.kycData?.nationality)
                  : '-',
              },
              {
                // Kept when the Sumsub/Onfido cards went: this flag lives on the person record the
                // admin persons endpoint returns, not in either vendor's API, so dropping the
                // vendor cards must not take it with them. A KYC officer decides on a file from it.
                key: 'hasCompletedBiometricCheck',
                label: 'Completed biometric check',
                span: 2,
                children: person.kycData?.hasCompletedBiometricCheck
                  ? 'Yes'
                  : 'No',
              },
              {
                key: 'hadSurname',
                label: (
                  <span>
                    Previous name or surname
                    <br />
                    (if you changed it)
                  </span>
                ),
                span: 2,
                children: person.kycData?.previousName ?? '-',
              },
            ]}
            colon={false}
          />
        </Card>

        <Card title="Residence address">
          <Descriptions
            column={4}
            items={[
              {
                key: 'country',
                label: 'Country',
                span: 4,
                children:
                  getCountryDataByCode(person.kycData?.address?.countryCode) ??
                  '-',
              },
              {
                key: 'city',
                label: 'City',
                span: 4,
                children: person.kycData?.address?.city ?? '-',
              },
              {
                key: 'address',
                label: 'Address',
                span: 4,
                children:
                  [
                    person.kycData?.address?.street,
                    person.kycData?.address?.houseNumber,
                  ]
                    .filter(Boolean)
                    .join(', ') ?? '-',
              },
              {
                key: 'zipCode',
                label: 'Zip code',
                span: 4,
                children: person.kycData?.address?.postalCode ?? '-',
              },
              {
                key: 'proofOfResidence',
                label: 'Proof of residence',
                span: 4,
                children: renderFiles(person, 'proof_of_residence'),
              },
            ]}
            colon={false}
          />
        </Card>

        <Card title="Business information">
          <Descriptions
            column={4}
            colon={false}
            items={[
              {
                key: 'tin',
                span: 4,
                label:
                  person.kycData?.address?.countryCode == 'LU'
                    ? 'National identification number'
                    : 'Tax identification number',
                children: person.kycData?.tin ?? '-',
              },
              {
                key: 'pep',
                label: 'Considered a Politically Exposed Person (PEP)?',
                span: 4,
                children: person.kycData?.pep ?? '-',
              },
              {
                key: 'blacklist',
                label:
                  'Works or has companies in countries blacklisted by the FATF, UN, EU, OECD or perceived as corrupt?',
                span: 4,
                children: person.kycData?.blacklist ?? '-',
              },
              {
                key: 'usaLink',
                label: 'Has any links to the USA?',
                span: 4,
                children: person.kycData?.usaLink ?? '-',
              },
              {
                key: 'experience',
                label:
                  'Describe your business/working experience and add LinkedIn profiles if you have it',
                span: 4,
                children: person.kycData?.experience ?? '-',
              },
              {
                key: 'CV',
                label: 'CV',
                span: 4,
                children: renderFiles(person, 'cv'),
              },
            ]}
          />
        </Card>

        <Card title="Source of wealth">
          <Descriptions
            column={4}
            items={[
              {
                key: 'sumAndDescription',
                label: 'How much will invest in their business?',
                span: 4,
                children:
                  person.kycData?.incomes?.[0]?.sumAndDescription ?? '-',
              },
              {
                key: 'type',
                label: 'Income type',
                span: 4,
                children: person.kycData?.incomes?.[0]?.type ?? '-',
              },
              {
                key: 'otherSourcesOfWealth',
                label: 'Other sources of wealth',
                span: 4,
                children: person.kycData?.otherSourcesOfWealth ?? '-',
              },
              {
                key: 'documents',
                label: 'Documents',
                span: 4,
                children: renderFiles(person, 'source_of_wealth'),
              },
            ]}
            colon={false}
          />
        </Card>
        <Card title="UBO number">
          <Descriptions
            column={4}
            items={[
              {
                key: 'uboNumber',
                label: 'UBO number',
                span: 4,
                children: uboNumber ? (
                  <Input
                    readOnly
                    value={uboNumber}
                    suffix={<CopyButton value={uboNumber} />}
                  />
                ) : (
                  '—'
                ),
              },
            ]}
          />
        </Card>
      </Flex>
    </S.Container>
  );
};
